/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { EChartsOption, LineSeriesOption } from 'echarts'

import { type PeriodWindow, buildMidnightMarkLineSeries, makeTimeAxis } from '@/components/charts/history/timeAxis'
import { GRID_SHARED } from '@/insights/assets/historyGraph/constants'
import { defaultPalette } from '@/insights/assets/historyGraph/palette'
import { formatHistoryDateTime } from '@/utils/formatDate'

import type { HistoryPoint } from './types'

/** One sensor's line on the humidity graph. */
export interface HumiditySeries {
  id: number
  name: string
  color: string
  points: HistoryPoint[]
}

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

/**
 * Each sensor's color on the temperature graph, so a sensor reads as the same line on both.
 * The temperature graph colors the points that have samples in name order (numeric-aware);
 * a sensor it did not draw takes the next color after them.
 */
export const sensorColors = (temperatureNames: string[], sensorNames: string[]): Map<string, string> => {
  const colors = defaultPalette.monitoringPoints
  const order = [...temperatureNames].sort(byName)
  for (const name of [...sensorNames].sort(byName)) {
    if (!order.includes(name)) order.push(name)
  }
  return new Map(order.map((name, i) => [name, colors[i % colors.length]]))
}

const formatPercent = (v: number) => `${Math.round(v)}%`

/** The humidity graph: one line per sensor on a 0–100% axis over the temperature graph's window. */
export const humidityChartOption = (
  series: HumiditySeries[],
  periodWindow: PeriodWindow,
  zone: string
): EChartsOption => {
  const lines: LineSeriesOption[] = series.map((s) => ({
    type: 'line',
    name: s.name,
    data: s.points.map((p) => [new Date(p.time).getTime(), p.value]),
    showSymbol: false,
    sampling: 'lttb',
    lineStyle: { width: 2, color: s.color },
    itemStyle: { color: s.color },
  }))
  const midnights = buildMidnightMarkLineSeries(periodWindow, zone) as LineSeriesOption | null
  return {
    animation: false,
    grid: { top: 10, bottom: 56, left: GRID_SHARED.left, right: GRID_SHARED.right },
    xAxis: makeTimeAxis({ gridIndex: 0, showLabel: true, periodWindow, zone }) as EChartsOption['xAxis'],
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: '%RH',
      nameLocation: 'middle',
      nameGap: 40,
      axisLabel: { formatter: formatPercent },
      splitLine: { show: true, lineStyle: { color: '#e5e7eb' } },
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      formatter: (params) => {
        const items = (Array.isArray(params) ? params : [params]).filter((p) => !p.seriesName?.startsWith('__'))
        if (!items.length) return ''
        const [time] = items[0].value as [number, number]
        return [
          formatHistoryDateTime(time, zone),
          ...items.map(
            (p) => `${p.marker as string} ${p.seriesName ?? ''}: ${formatPercent((p.value as number[])[1])}`
          ),
        ].join('<br/>')
      },
    },
    series: midnights ? [...lines, midnights] : lines,
  }
}
